/**
 * Phase 3e — Employer quotes dashboard MVP.
 *
 * Route: /portal/employer/quotes
 *
 * Surfaces every saved quote tied to the current session email so the
 * prospect can find their way back after bouncing out. Works with EITHER
 * session cookie:
 *   - laguna_proposal_session  (quote-only prospect, 20-min sliding TTL)
 *   - laguna_team_session      (converted employer)
 *
 * The backend's GET /api/employer/quotes dependency picks whichever
 * cookie is present and returns the same shape. Falls back to /apply
 * when no session is detected.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PortalChrome from "../components/portal/PortalChrome";
import { employerQuotes, employerNewQuote, formatApiError } from "../lib/api";
import { track } from "../lib/analytics";

import "./PortalEmployerQuotes.css";

function fmtCurrency(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return "—";
    return `$${Math.round(n).toLocaleString()}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch (_e) { return iso; }
}

/* -------------------------------------------------------------------- */
/* FunnelRing — SVG donut. Background track = eligible total. Coral arc */
/* = completion count. Centered label + % sit inside the ring. Used     */
/* both in the aggregate bar (clickable) and in the drill-down modal    */
/* rows (compact, non-interactive).                                     */
/* -------------------------------------------------------------------- */
const FUNNEL_KEYS = ["invited", "registered", "completed_app", "purchased"];

function FunnelRing({
    label, count, total, size = 116, stroke = 10, compact = false,
}) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = total > 0 ? Math.min(1, count / total) : 0;
    const dash = c * pct;
    const pctDisplay = total > 0 ? Math.round(pct * 100) : 0;
    return (
        <div className={`fn-ring ${compact ? "is-compact" : ""}`}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" stroke="rgba(35,31,26,0.08)" strokeWidth={stroke}
                />
                {pct > 0 && (
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke="#ff5a36" strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={`${dash} ${c - dash}`}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                )}
            </svg>
            <div className="fn-ring-center">
                <div className="fn-ring-count" data-testid="funnel-count">{count}</div>
                <div className="fn-ring-total mono">
                    / {total} · {pctDisplay}%
                </div>
            </div>
            <div className="fn-ring-label mono">{label}</div>
        </div>
    );
}

function FunnelSection({ totals, quotes, onDrillDown }) {
    const { t } = useTranslation();
    const items = [
        { key: "invited", label: t("employerQuotes.funnel.invited", { defaultValue: "Invited" }) },
        { key: "registered", label: t("employerQuotes.funnel.registered", { defaultValue: "Registered" }) },
        { key: "completed_app", label: t("employerQuotes.funnel.completedApp", { defaultValue: "Completed app" }) },
        { key: "purchased", label: t("employerQuotes.funnel.purchased", { defaultValue: "Purchased" }) },
    ];
    const multiCompany = (quotes?.length || 0) > 1;
    return (
        <section className="fn-section" data-testid="employer-funnel-section">
            <div className="mono eq-eyebrow">
                {t("employerQuotes.funnel.eyebrow", { defaultValue: "Rollout momentum" })}
            </div>
            <h2 className="fn-section-title">
                {t("employerQuotes.funnel.title", { defaultValue: "Where your team is in the funnel." })}
            </h2>
            <p className="fn-section-sub">
                {t("employerQuotes.funnel.sub", {
                    defaultValue: "Each ring is a step in the rollout. Coral arc shows how many of your eligible employees have reached that step.",
                })}
            </p>
            <div className="fn-grid" data-testid="funnel-grid">
                {items.map((it) => (
                    <button
                        key={it.key}
                        type="button"
                        className="fn-cell"
                        onClick={() => {
                            track("funnel_ring_click", { step: it.key });
                            onDrillDown(it.key);
                        }}
                        disabled={!multiCompany}
                        aria-label={`${it.label} — ${totals[it.key]} of ${totals.eligible}${multiCompany ? "; click for per-company breakdown" : ""}`}
                        data-testid={`funnel-cell-${it.key}`}
                    >
                        <FunnelRing
                            label={it.label}
                            count={totals[it.key] || 0}
                            total={totals.eligible || 0}
                        />
                        {multiCompany && (
                            <span className="fn-cell-hint mono">
                                {t("employerQuotes.funnel.perCompany", { defaultValue: "By company →" })}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </section>
    );
}

function FunnelDrillDownModal({ open, onClose, stepKey, quotes }) {
    const { t } = useTranslation();
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);
    if (!open || !stepKey) return null;
    const stepLabel = {
        invited: t("employerQuotes.funnel.invited", { defaultValue: "Invited" }),
        registered: t("employerQuotes.funnel.registered", { defaultValue: "Registered" }),
        completed_app: t("employerQuotes.funnel.completedApp", { defaultValue: "Completed app" }),
        purchased: t("employerQuotes.funnel.purchased", { defaultValue: "Purchased" }),
    }[stepKey];

    return (
        <div
            className="eq-modal-overlay"
            role="presentation"
            onClick={(e) => e.target === e.currentTarget && onClose()}
            data-testid="funnel-drilldown-overlay"
        >
            <div
                className="eq-modal fn-drilldown-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="fn-drill-title"
                data-testid="funnel-drilldown-modal"
            >
                <button
                    type="button"
                    className="eq-modal-close"
                    onClick={onClose}
                    aria-label={t("employerQuotes.newQuote.close", { defaultValue: "Close" })}
                    data-testid="funnel-drilldown-close"
                >×</button>
                <p className="mono eq-eyebrow">
                    {t("employerQuotes.funnel.drillEyebrow", { defaultValue: "By company" })}
                </p>
                <h2 className="eq-modal-title" id="fn-drill-title">{stepLabel}</h2>
                <p className="eq-modal-sub">
                    {t("employerQuotes.funnel.drillSub", {
                        defaultValue: "How each company is pacing through this step.",
                    })}
                </p>
                <ul className="fn-drill-list" data-testid="funnel-drilldown-list">
                    {quotes.map((q) => {
                        const count = q.funnel?.[stepKey] || 0;
                        const total = q.eligible_employees || 0;
                        return (
                            <li
                                key={q.proposal_id}
                                className="fn-drill-row"
                                data-testid={`funnel-drilldown-row-${q.proposal_id}`}
                            >
                                <FunnelRing
                                    label=""
                                    count={count}
                                    total={total}
                                    size={64}
                                    stroke={7}
                                    compact
                                />
                                <div className="fn-drill-meta">
                                    <div className="fn-drill-company">
                                        {q.company_name || "—"}
                                    </div>
                                    <div className="fn-drill-sub mono">
                                        {q.situs_state_name || q.situs_state_code || "—"}
                                        {q.zip_code ? ` · ${q.zip_code}` : ""}
                                        {" · "}
                                        <span>{count}/{total}</span>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

function QuoteCard({ quote }) {
    const { t } = useTranslation();
    const resumeUrl = `/portal/proposal/${quote.proposal_id}`;
    return (
        <article
            className="eq-card"
            data-testid={`employer-quote-card-${quote.proposal_id}`}
        >
            <header className="eq-card-head">
                <div>
                    <div className="mono eq-eyebrow">
                        {t("employerQuotes.card.eyebrow", { defaultValue: "Quote" })}
                        {" · "}
                        {fmtDate(quote.created_at)}
                    </div>
                    <h3 className="eq-company">{quote.company_name || t("employerQuotes.card.untitled", { defaultValue: "Untitled quote" })}</h3>
                </div>
                <span
                    className={`eq-pill eq-pill-${quote.is_application_started ? "started" : "active"}`}
                    data-testid="employer-quote-status"
                >
                    {quote.is_application_started
                        ? t("employerQuotes.card.statusStarted", { defaultValue: "Application started" })
                        : t("employerQuotes.card.statusActive", { defaultValue: "Active" })}
                </span>
            </header>

            <dl className="eq-stats">
                <div className="eq-stat">
                    <dt>{t("employerQuotes.card.eligible", { defaultValue: "Eligible employees" })}</dt>
                    <dd data-testid="employer-quote-eligible">{quote.eligible_employees || 0}</dd>
                </div>
                <div className="eq-stat">
                    <dt>{t("employerQuotes.card.costToCompany", { defaultValue: "Cost to company" })}</dt>
                    <dd data-testid="employer-quote-cost">{fmtCurrency(quote.cost_to_company)}</dd>
                </div>
                <div className="eq-stat">
                    <dt>{t("employerQuotes.card.projectedAnnual", { defaultValue: "Projected annual premium" })}</dt>
                    <dd data-testid="employer-quote-annual">{fmtCurrency(quote.projected_annual_premium)}</dd>
                </div>
                <div className="eq-stat">
                    <dt>{t("employerQuotes.card.situs", { defaultValue: "Situs state" })}</dt>
                    <dd data-testid="employer-quote-state">
                        {quote.situs_state_name || quote.situs_state_code || "—"}
                    </dd>
                </div>
            </dl>

            <footer className="eq-actions">
                <Link
                    to={resumeUrl}
                    className="eq-cta-primary"
                    onClick={() => track("employer_quotes_resume_click", { proposal_id: quote.proposal_id })}
                    data-testid="employer-quote-resume"
                >
                    {quote.is_application_started
                        ? t("employerQuotes.card.ctaView", { defaultValue: "View" })
                        : t("employerQuotes.card.ctaResume", { defaultValue: "Resume" })}
                    &nbsp;→
                </Link>
            </footer>
        </article>
    );
}

function NewQuoteModal({ open, onClose, onCreated }) {
    const { t } = useTranslation();
    const [values, setValues] = useState({ companyName: "", zipCode: "", employeeCount: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState("");
    const firstRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        setTimeout(() => firstRef.current?.focus(), 20);
        track("employer_new_quote_modal_view", {});
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    function validate() {
        const e = {};
        if (!values.companyName.trim()) e.companyName = t("errors.required", { defaultValue: "Required" });
        if (!/^\d{5}$/.test(values.zipCode.trim()))
            e.zipCode = t("employers.quote.errorZip", { defaultValue: "Enter a valid 5-digit ZIP." });
        const ec = parseInt(values.employeeCount, 10);
        if (!ec || ec < 1) e.employeeCount = t("errors.required", { defaultValue: "Required" });
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function handleSubmit(ev) {
        ev.preventDefault();
        setSubmitErr("");
        if (!validate()) return;
        setSubmitting(true);
        try {
            const res = await employerNewQuote({
                companyName: values.companyName.trim(),
                zipCode: values.zipCode.trim(),
                employeeCount: parseInt(values.employeeCount, 10),
            });
            track("employer_new_quote_created", { proposal_id: res?.proposalId });
            onCreated?.(res);
            // Reset for next time.
            setValues({ companyName: "", zipCode: "", employeeCount: "" });
            setErrors({});
        } catch (err) {
            setSubmitErr(formatApiError(err) || t("employerQuotes.newQuote.errorGeneric", {
                defaultValue: "We couldn't create that quote. Please try again.",
            }));
            track("employer_new_quote_error", { status: err?.response?.status });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="eq-modal-overlay"
            role="presentation"
            onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
            data-testid="new-quote-modal-overlay"
        >
            <div
                className="eq-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="eq-new-title"
                data-testid="new-quote-modal"
            >
                <button
                    type="button"
                    className="eq-modal-close"
                    onClick={onClose}
                    disabled={submitting}
                    aria-label={t("employerQuotes.newQuote.close", { defaultValue: "Close" })}
                    data-testid="new-quote-modal-close"
                >
                    ×
                </button>
                <p className="mono eq-eyebrow">
                    {t("employerQuotes.newQuote.eyebrow", { defaultValue: "Another company" })}
                </p>
                <h2 className="eq-modal-title" id="eq-new-title">
                    {t("employerQuotes.newQuote.title", { defaultValue: "Quote a second company." })}
                </h2>
                <p className="eq-modal-sub">
                    {t("employerQuotes.newQuote.sub", {
                        defaultValue: "We'll reuse your contact info. Just tell us the basics.",
                    })}
                </p>

                {submitErr && (
                    <div className="eq-modal-error" role="status" data-testid="new-quote-modal-error">
                        {submitErr}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="eq-modal-form" noValidate>
                    <label className="eq-modal-label mono">
                        {t("employers.quote.labelCompany", { defaultValue: "Company name" })}
                    </label>
                    <input
                        ref={firstRef}
                        type="text"
                        className="eq-modal-input"
                        value={values.companyName}
                        onChange={(e) => setValues((v) => ({ ...v, companyName: e.target.value }))}
                        data-testid="new-quote-company"
                        maxLength={200}
                    />
                    {errors.companyName && <span className="eq-modal-field-error">{errors.companyName}</span>}

                    <label className="eq-modal-label mono">
                        {t("employers.quote.labelZip", { defaultValue: "Company ZIP code" })}
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{5}"
                        maxLength={5}
                        className="eq-modal-input"
                        value={values.zipCode}
                        onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
                            setValues((v) => ({ ...v, zipCode: digits }));
                        }}
                        data-testid="new-quote-zip"
                    />
                    {errors.zipCode && <span className="eq-modal-field-error">{errors.zipCode}</span>}

                    <label className="eq-modal-label mono">
                        {t("employers.quote.labelEmployees", { defaultValue: "Eligible employees" })}
                    </label>
                    <input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        className="eq-modal-input"
                        value={values.employeeCount}
                        onChange={(e) => setValues((v) => ({ ...v, employeeCount: e.target.value.replace(/\D/g, "") }))}
                        data-testid="new-quote-employees"
                    />
                    {errors.employeeCount && <span className="eq-modal-field-error">{errors.employeeCount}</span>}

                    <div className="eq-modal-actions">
                        <button
                            type="button"
                            className="eq-modal-cancel"
                            onClick={onClose}
                            disabled={submitting}
                            data-testid="new-quote-cancel"
                        >
                            {t("employerQuotes.newQuote.cancel", { defaultValue: "Cancel" })}
                        </button>
                        <button
                            type="submit"
                            className="eq-cta-primary"
                            disabled={submitting}
                            data-testid="new-quote-submit"
                        >
                            {submitting
                                ? t("employerQuotes.newQuote.submitting", { defaultValue: "Generating…" })
                                : t("employerQuotes.newQuote.submit", { defaultValue: "Generate quote" })}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function PortalEmployerQuotes() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [modalOpen, setModalOpen] = useState(false);
    const [drillStep, setDrillStep] = useState(null);

    async function refreshQuotes() {
        try {
            const data = await employerQuotes();
            setState({ loading: false, error: null, data });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                navigate("/employer-signin?next=/portal/employer/quotes", { replace: true });
                return;
            }
            setState({ loading: false, error: "load_failed", data: null });
        }
    }

    useEffect(() => {
        track("employer_quotes_view", {});
        refreshQuotes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PortalChrome actorType="employer" displayName={state.data?.email}>
            <section className="eq-wrap" data-testid="employer-quotes-page">
                <header className="eq-hero">
                    <div className="mono eq-eyebrow">
                        {t("employerQuotes.hero.eyebrow", { defaultValue: "Your saved quotes" })}
                    </div>
                    <h1 className="eq-title">
                        {t("employerQuotes.hero.title", { defaultValue: "Pick up where you left off." })}
                    </h1>
                    <p className="eq-sub">
                        {t("employerQuotes.hero.sub", {
                            defaultValue: "Every quote you've generated lives here. Resume any one to review your range, adjust riders, or kick off the application.",
                        })}
                    </p>
                </header>

                {state.loading && (
                    <div className="eq-loading mono" data-testid="employer-quotes-loading">
                        {t("employerQuotes.loading", { defaultValue: "Loading your quotes…" })}
                    </div>
                )}

                {state.error && (
                    <div className="eq-error" data-testid="employer-quotes-error">
                        {t("employerQuotes.error", {
                            defaultValue: "We couldn't load your quotes right now. Try refreshing the page.",
                        })}
                    </div>
                )}

                {state.data && state.data.quotes.length === 0 && (
                    <div className="eq-empty" data-testid="employer-quotes-empty">
                        <p>
                            {t("employerQuotes.empty.body", {
                                defaultValue: "You haven't generated a quote yet.",
                            })}
                        </p>
                        <Link to="/employers" className="eq-cta-primary" data-testid="employer-quotes-empty-cta">
                            {t("employerQuotes.empty.cta", { defaultValue: "Get your first quote" })}
                            &nbsp;→
                        </Link>
                    </div>
                )}

                {state.data && state.data.quotes.length > 0 && (
                    <>
                        <FunnelSection
                            totals={state.data.totals || { eligible: 0, invited: 0, registered: 0, completed_app: 0, purchased: 0 }}
                            quotes={state.data.quotes}
                            onDrillDown={(step) => setDrillStep(step)}
                        />
                        <div className="eq-grid" data-testid="employer-quotes-grid">
                            {state.data.quotes.map((q) => (
                                <QuoteCard key={q.proposal_id} quote={q} />
                            ))}
                        </div>
                    </>
                )}

                <footer className="eq-footer-cta">
                    <button
                        type="button"
                        className="eq-cta-secondary"
                        data-testid="employer-quotes-new"
                        onClick={() => {
                            track("employer_quotes_new_click", {});
                            setModalOpen(true);
                        }}
                    >
                        {t("employerQuotes.footer.newQuote", { defaultValue: "+ Request another quote" })}
                    </button>
                </footer>
            </section>
            <NewQuoteModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={async () => {
                    setModalOpen(false);
                    await refreshQuotes();
                }}
            />
            <FunnelDrillDownModal
                open={!!drillStep}
                stepKey={drillStep}
                quotes={state.data?.quotes || []}
                onClose={() => setDrillStep(null)}
            />
        </PortalChrome>
    );
}
